import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';

import {
  CardConfig,
  CardData,
  CardState,
  Note,
  StateConfiguration,
} from '@shared-models';

import { CardBodyComponent } from './card-body/card-body.component';
import { CardFooterComponent } from './card-footer/card-footer.component';
import { CardHeaderComponent } from './card-header/card-header.component';

@Component({
  selector: 'v-one-card',
  imports: [
    NgClass,
    CardHeaderComponent,
    CardBodyComponent,
    CardFooterComponent,
  ],
  templateUrl: './v-one-card.component.html',
  styleUrl: './v-one-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOneCardComponent {
  // Signals for reactive state management
  protected readonly data = signal<CardData | null>(null);
  protected readonly config = signal<CardConfig>({
    showActions: true,
  });

  // Input setters that update signals
  @Input() set cardData(value: CardData) {
    this.data.set(value);
  }

  @Input() set cardConfig(value: CardConfig) {
    this.config.update((current) => ({ ...current, ...value }));
  }

  // Outputs
  @Output() menuAction = new EventEmitter<string>();
  @Output() dropdownAction = new EventEmitter<string>();
  @Output() notesDetailClick = new EventEmitter<Note[]>();

  // Computed signals
  protected readonly cardClasses = computed(() => {
    const config = this.config();
    const data = this.data();
    const classes = [
      'v-one-card',
      `v-one-card--${data?.state}`,
      `v-one-card--${data?.configuration}`,
    ];

    if (config.customClass) {
      classes.push(config.customClass);
    }

    return classes.join(' ');
  });

  protected readonly stateConfig = computed(() => {
    const state = this.data()?.state;
    return this.getStateConfiguration(state);
  });

  protected handleMenuAction(action: string): void {
    this.menuAction.emit(action);
  }

  protected handleDropdownAction(action: string): void {
    this.dropdownAction.emit(action);
  }

  protected handleNotesDetailClick(notes: Note[]): void {
    this.notesDetailClick.emit(notes);
  }

  private getStateConfiguration(state?: CardState): StateConfiguration {
    const stateMap: Record<CardState, StateConfiguration> = {
      order: {
        icon: 'order',
        color: 'var(--color-info-700)',
        label: 'Order',
      },
      delivery: {
        icon: 'delivery',
        color: 'var(--color-warning-700)',
        label: 'Delivery',
      },
      'invoice-without-notes': {
        icon: 'invoice',
        color: 'var(--color-success-700)',
        label: 'Invoice',
      },
      'invoice-with-notes': {
        icon: 'invoice',
        color: 'var(--color-success-700)',
        label: 'Invoice',
      },
      rejected: {
        icon: 'error',
        color: 'var(--color-danger-700)',
        label: 'Rejected',
      },
      // These four states render as a chip in the header instead of an
      // icon + title (see CardHeaderComponent.getChipConfig) — the entries
      // below only exist to satisfy Record<CardState, ...> exhaustiveness.
      'invoice-unpaid': {
        icon: 'wallet',
        color: 'var(--color-danger-700)',
        label: 'card.paymentStatus.unpaid',
      },
      'invoice-partial': {
        icon: 'wallet',
        color: 'var(--color-warning-700)',
        label: 'card.paymentStatus.partial',
      },
      'invoice-fully-paid': {
        icon: 'approved',
        color: 'var(--color-success-700)',
        label: 'card.paymentStatus.paid',
      },
      'invoice-voided': {
        icon: 'error',
        color: 'var(--color-gray-500)',
        label: 'card.paymentStatus.voided',
      },
    };

    return state ? stateMap[state] : stateMap['order'];
  }
}
